import { useEffect, useRef, useState } from 'react';

import { Botao, Modal } from '../../../componentes/Flutuante';
import { Icone } from '../../../componentes/Icone';

const LADO = 300;
const SAIDA = 512;

/**
 * Ajustar avatar (U19): arrasta a foto dentro do círculo, aproxima e gira;
 * "Aplicar" devolve um PNG quadrado de 512 px (data URI).
 */
export function RecortarAvatar({
  imagem,
  aoAplicar,
  aoFechar,
  redondo = true,
}: {
  imagem: string;
  aoAplicar: (dataUri: string) => void;
  aoFechar: () => void;
  redondo?: boolean;
}) {
  const [foto, setFoto] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [giro, setGiro] = useState(0);
  const [posicao, setPosicao] = useState({ x: 0, y: 0 });
  const arrasto = useRef<{
    x: number;
    y: number;
    px: number;
    py: number;
  } | null>(null);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setFoto(img);
    img.src = imagem;
  }, [imagem]);

  // Escala que cobre o quadro (a foto nunca deixa buraco no círculo).
  const deitada = giro % 180 !== 0;
  const largura = foto ? (deitada ? foto.height : foto.width) : 1;
  const altura = foto ? (deitada ? foto.width : foto.height) : 1;
  const base = LADO / Math.min(largura, altura);

  const aplicar = () => {
    if (!foto) return;
    const tela = document.createElement('canvas');
    tela.width = SAIDA;
    tela.height = SAIDA;
    const ctx = tela.getContext('2d');
    if (!ctx) return;
    const fator = SAIDA / LADO;
    ctx.translate(SAIDA / 2 + posicao.x * fator, SAIDA / 2 + posicao.y * fator);
    ctx.rotate((giro * Math.PI) / 180);
    const escala = base * zoom * fator;
    ctx.drawImage(
      foto,
      (-foto.width * escala) / 2,
      (-foto.height * escala) / 2,
      foto.width * escala,
      foto.height * escala,
    );
    aoAplicar(tela.toDataURL('image/png'));
  };

  return (
    <Modal
      titulo="Ajustar avatar"
      descricao="Arraste a foto para posicionar dentro do círculo."
      aoFechar={aoFechar}
      largura={420}
      rodape={
        <>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao data-foco-inicial disabled={!foto} onClick={aplicar}>
            Aplicar
          </Botao>
        </>
      }
    >
      <div
        className="relative mx-auto cursor-grab touch-none overflow-hidden bg-rail active:cursor-grabbing"
        style={{ width: LADO, height: LADO }}
        onPointerDown={(evento) => {
          evento.currentTarget.setPointerCapture(evento.pointerId);
          arrasto.current = {
            x: evento.clientX,
            y: evento.clientY,
            px: posicao.x,
            py: posicao.y,
          };
        }}
        onPointerMove={(evento) => {
          const inicio = arrasto.current;
          if (!inicio) return;
          setPosicao({
            x: inicio.px + evento.clientX - inicio.x,
            y: inicio.py + evento.clientY - inicio.y,
          });
        }}
        onPointerUp={() => {
          arrasto.current = null;
        }}
      >
        {foto && (
          <img
            src={imagem}
            alt=""
            draggable={false}
            className="pointer-events-none absolute top-1/2 left-1/2 max-w-none"
            style={{
              width: foto.width * base * zoom,
              height: foto.height * base * zoom,
              transform: `translate(-50%, -50%) translate(${posicao.x}px, ${posicao.y}px) rotate(${giro}deg)`,
            }}
          />
        )}
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 ${redondo ? 'rounded-full' : 'rounded-xl'} shadow-[0_0_0_999px_rgba(5,6,8,.6)] outline-2 outline-white/70`}
        />
      </div>
      <div className="flex items-center gap-3">
        <Icone nome="imagem" tamanho={16} className="text-texto-3" />
        <input
          type="range"
          aria-label="Aproximar"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(evento) => setZoom(Number(evento.target.value))}
          className="grow accent-[var(--cor-destaque)]"
        />
        <Icone nome="imagem" tamanho={22} className="text-texto-3" />
        <button
          type="button"
          onClick={() => setGiro((atual) => (atual + 90) % 360)}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-ativo px-3 text-sm font-semibold hover:brightness-110"
        >
          Girar
        </button>
      </div>
    </Modal>
  );
}
